/* Which shell the app is running in: a browser tab, the Windows app or the Android app. */

export type Shell = "browser" | "android" | "desktop";

declare global {
  interface Window {
    Capacitor?: { isNativePlatform?: () => boolean };
    /** Set by the Windows app's preload script (see desktop/src/preload.cjs). */
    pboDesktop?: {
      version: string;
      saveFile: (filename: string, data: ArrayBuffer) => Promise<string | null>;
      openExternal: (url: string) => Promise<void>;
    };
  }
}

export function shell(): Shell {
  if (typeof window === "undefined") return "browser";
  if (window.pboDesktop) return "desktop";
  if (window.Capacitor?.isNativePlatform?.() === true) return "android";
  return "browser";
}

export function isNativeShell(): boolean {
  return shell() !== "browser";
}

/**
 * Open a page (Google's consent screen) outside the app window, so the app keeps its place.
 * The Windows app hands it to the system browser. Android navigates, and Capacitor sends any
 * host that is not the app's own to the system browser. A browser opens the tab before the
 * address is fetched, so pop-up blockers allow it, and navigates in place if it was blocked.
 */
export async function openOutside(load: () => Promise<string>): Promise<void> {
  const current = shell();
  if (current === "desktop" && window.pboDesktop) {
    await window.pboDesktop.openExternal(await load());
    return;
  }
  if (current === "android") {
    window.location.assign(await load());
    return;
  }
  const tab = window.open("", "_blank");
  if (tab) tab.opener = null;
  try {
    const url = await load();
    if (tab === null) window.location.assign(url);
    else tab.location.href = url;
  } catch (error) {
    tab?.close();
    throw error;
  }
}

/** What to call the app in a sentence. */
export function shellName(): string {
  switch (shell()) {
    case "android":
      return "the Android app";
    case "desktop":
      return "the Windows app";
    default:
      return "the browser";
  }
}
